import { useCallback, useEffect, useState } from "react";
import { DayPicker } from "react-day-picker";

import { Day } from "@bokio/mobile-web-shared/core/model/model";
import { mergeClassNames } from "@bokio/utils/classes";

import { castDateToDay, castValidDayToDate, useReactDayPickerStylingProps } from "./useReactDayPickerStylingProps";

import type { Matcher } from "react-day-picker";

import * as styles from "./dayInput.scss";

interface DatePickerProps {
	value?: Day;
	monthToStartFrom?: Day;
	onChange: (date: Day) => void;
	maxMonth?: Date;
	minMonth?: Date;
	disabledDays?: Matcher[];
	alignCal?: "left" | "right";
	openAbove?: boolean;
}

export const DatePicker = ({
	onChange,
	value,
	monthToStartFrom,
	minMonth,
	maxMonth,
	disabledDays,
	alignCal,
	openAbove,
}: DatePickerProps) => {
	const getCurrentShowingMonthByValue = useCallback(() => {
		return value ?? monthToStartFrom ?? Day.today();
	}, [monthToStartFrom, value]);
	// This state is mainly to handle so when clicking on a day that's outside of the current month,
	// the calendar will automatically switch to the month of the day.
	const [currentShowingMonth, setCurrentShowingMonth] = useState<Day>(() => getCurrentShowingMonthByValue());

	const reactDayPickerStylingProps = useReactDayPickerStylingProps({
		setCurrentShowingMonth,
		minMonth,
		maxMonth,
	});

	useEffect(() => {
		// eslint-disable-next-line @eslint-react/set-state-in-effect
		setCurrentShowingMonth(getCurrentShowingMonthByValue());
	}, [getCurrentShowingMonthByValue]);

	const overlayStyle = mergeClassNames(
		styles.overlay,
		styles.overlayShow,
		alignCal === "left" && styles.overlayLeft,
		openAbove && styles.overlayAbove,
	);

	return (
		<div className={overlayStyle}>
			<DayPicker
				{...reactDayPickerStylingProps}
				mode="single"
				selected={castValidDayToDate(value)}
				onSelect={(selected, _triggerDate, _modifiers, event) => {
					event.stopPropagation();
					const selectedDay = castDateToDay(selected);
					if (selectedDay) {
						setCurrentShowingMonth(selectedDay);
						onChange(selectedDay);
					}
				}}
				// SS 2025-08-07
				// Using react-day-picker's defaultMonth prop will let react-day-picker control the month shown,
				// but if you click a day that's outside of the current month (allowed because we always show 6 weeks),
				// it will not switch to that month after re-opening the calendar.
				// Therefore here we make the month controlled by the component state instead.
				defaultMonth={undefined}
				month={castValidDayToDate(currentShowingMonth)}
				onMonthChange={month => {
					const selectedMonth = castDateToDay(month);
					if (selectedMonth) {
						setCurrentShowingMonth(selectedMonth);
					}
				}}
				startMonth={minMonth}
				endMonth={maxMonth}
				disabled={disabledDays}
			/>
		</div>
	);
};
